// =============================================================================
// Web Canvas Store — Thin wrapper around @event-tools/canvas-core
// =============================================================================
//
// Uses the shared createCanvasStore factory. Web-specific additions
// (Convex persistence) are handled by consuming components, not this store.
// =============================================================================

import { createCanvasStore } from '@event-tools/canvas-core';
import type { CanvasState, CanvasStore, FlyerProject } from '@event-tools/canvas-core';

// Create the core store
const coreStore = createCanvasStore();

// Backward compatibility for older web components that predate canvas-core.
type CompatStore = Omit<CanvasStore, 'canUndo' | 'canRedo' | 'loadProject' | 'moveLayer'> & {
  selectedLayerId: string | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  getCanvasState: () => CanvasState;
  loadProject: (project: FlyerProject | CanvasState) => void;
  moveLayer: (id: string, directionOrIndex: 'up' | 'down' | number) => void;
};

function moveLayerCompat(id: string, directionOrIndex: 'up' | 'down' | number) {
  const state = coreStore.getState();
  if (typeof directionOrIndex === 'number') {
    state.moveLayer(id, directionOrIndex);
    return;
  }

  const currentIndex = state.canvasState.layers.findIndex((layer) => layer.id === id);
  if (currentIndex < 0) return;
  const nextIndex = directionOrIndex === 'up'
    ? Math.min(state.canvasState.layers.length - 1, currentIndex + 1)
    : Math.max(1, currentIndex - 1);
  state.moveLayer(id, nextIndex);
}

function loadProjectCompat(project: FlyerProject | CanvasState) {
  const state = coreStore.getState();
  if ('layers' in project) {
    state.setCanvasState(project);
    state.deselectAll();
    return;
  }
  state.loadProject(project);
}

function toCompatStore(state: CanvasStore): CompatStore {
  return {
    ...state,
    selectedLayerId: state.selectedLayerIds[0] ?? null,
    canUndo: () => coreStore.getState().canUndo,
    canRedo: () => coreStore.getState().canRedo,
    getCanvasState: () => coreStore.getState().canvasState,
    loadProject: loadProjectCompat,
    moveLayer: moveLayerCompat,
  };
}

export const useCanvasStore = ((selector?: (state: CompatStore) => unknown) => {
  if (selector) {
    return coreStore((state) => {
      return selector(toCompatStore(state));
    });
  }
  return coreStore((state) => toCompatStore(state));
}) as {
  (): CompatStore;
  <T>(selector: (state: CompatStore) => T): T;
  getState: () => CompatStore;
  setState: typeof coreStore.setState;
  subscribe: typeof coreStore.subscribe;
};

useCanvasStore.getState = () => {
  const state = coreStore.getState();
  return toCompatStore(state);
};
useCanvasStore.setState = coreStore.setState;
useCanvasStore.subscribe = coreStore.subscribe;

// Re-export types
export type { CanvasStore } from '@event-tools/canvas-core';
